import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, CheckCircle2, Circle, Clock, Flame, Plus, Search, Sparkles, Users, X, XCircle, CheckSquare, Square } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, storage } from "@/lib/api";
import { isManagerRole, useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { Avatar, Button, Card, ChipRow, Input, Overline } from "@/components/app/Primitives";
import { StackPage } from "@/components/app/Layout";

const PRIORITIES = [{ key: "low", label: "Low" }, { key: "medium", label: "Medium" }, { key: "high", label: "High" }, { key: "critical", label: "Critical" }];
const RECURRENCE = [{ key: "none", label: "One-time" }, { key: "daily", label: "Daily" }, { key: "weekly", label: "Weekly" }, { key: "monthly", label: "Monthly" }];
const RECENT_KEY_PREFIX = "assignoo.recent-assignees.";
const pad = (n) => String(n).padStart(2, "0");
const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export default function TaskNew() {
  const toast = useToast();
  const navigate = useNavigate();
  const { activeWorkspace, user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDesc] = useState("");
  const [priority, setPriority] = useState("medium");
  const [recurrence, setRecurrence] = useState("none");
  const [deadline, setDeadline] = useState(null);
  const [includeTime, setIncludeTime] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [nextItem, setNextItem] = useState("");
  const [members, setMembers] = useState([]);
  const [recentIds, setRecentIds] = useState([]);
  const [search, setSearch] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const canAssignOthers = isManagerRole(activeWorkspace?.role || "employee");
  const recentKey = activeWorkspace ? `${RECENT_KEY_PREFIX}${activeWorkspace.id}` : "";

  useEffect(() => {
    (async () => {
      if (!activeWorkspace) return;
      try { setMembers(await api(`/workspaces/${activeWorkspace.id}/members`)); } catch { /* noop */ }
      try { const r = storage.get(recentKey, ""); if (r) setRecentIds(JSON.parse(r)); } catch { /* noop */ }
      if (!canAssignOthers && user) setAssignees([user.id]);
    })();
  }, [activeWorkspace?.id]); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => m.full_name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q) || (m.designation || "").toLowerCase().includes(q) || m.role.toLowerCase().includes(q));
  }, [members, search]);
  const recentMembers = useMemo(() => {
    const map = new Map(members.map((m) => [m.user_id, m]));
    return recentIds.map((id) => map.get(id)).filter(Boolean).slice(0, 6);
  }, [members, recentIds]);

  const toggleAssignee = (id) => {
    if (!canAssignOthers && user && id !== user.id) return toast.show("Only managers can assign to other members", "error");
    setAssignees((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };
  const aiDescribe = async () => {
    if (!title.trim()) return toast.show("Enter a title first", "error");
    setAiBusy(true);
    try { setDesc((await api("/ai/task-description", { method: "POST", body: { title: title.trim() } })).description); toast.show("AI description generated", "success"); }
    catch (e) { toast.show(e instanceof Error ? e.message : "AI failed", "error"); } finally { setAiBusy(false); }
  };
  const aiPriority = async () => {
    if (!title.trim()) return toast.show("Enter a title first", "error");
    setAiBusy(true);
    try { const r = await api("/ai/priority-suggest", { method: "POST", body: { title: title.trim(), description } }); setPriority(r.priority); toast.show(`AI suggested: ${r.priority}`, "success"); }
    catch (e) { toast.show(e instanceof Error ? e.message : "AI failed", "error"); } finally { setAiBusy(false); }
  };
  const addItem = () => { if (nextItem.trim()) { setItems((p) => [...p, nextItem.trim()]); setNextItem(""); } };

  const submit = async () => {
    if (!activeWorkspace) return;
    if (!title.trim()) return toast.show("Title is required", "error");
    if (assignees.length === 0) return toast.show("Select at least one assignee", "error");
    setBusy(true);
    try {
      const t = await api("/tasks", {
        method: "POST",
        body: {
          workspace_id: activeWorkspace.id, title: title.trim(), description: description.trim(), priority, recurrence,
          deadline: deadline ? (includeTime ? deadline.toISOString() : localDate(deadline)) : undefined,
          checklist: items, assignee_ids: assignees,
        },
      });
      storage.set(recentKey, JSON.stringify([...assignees, ...recentIds.filter((id) => !assignees.includes(id))].slice(0, 10)));
      toast.show("Task created", "success");
      navigate(`/task/${t.id}`, { replace: true });
    } catch (e) { toast.show(e instanceof Error ? e.message : "Failed to create task", "error"); } finally { setBusy(false); }
  };

  const timeValue = deadline ? `${pad(deadline.getHours())}:${pad(deadline.getMinutes())}` : "09:00";
  const aiBtn = "flex items-center gap-1.5 rounded-lg border border-a-primary px-2.5 py-1.5 text-xs font-bold text-a-primary hover:bg-a-dim disabled:opacity-50";

  return (
    <StackPage title="New task" backIcon={X} testID="task-new-page">
      <Card>
        <div className="space-y-3">
          <Input testID="task-title-input" label="Title" placeholder="e.g. Prepare Q4 pitch deck" value={title} onChangeText={setTitle} />
          <div>
            <Input testID="task-description-input" label="Description" placeholder="What needs to be done?" value={description} onChangeText={setDesc} multiline rows={4} />
            <div className="mt-2 flex gap-2">
              <button onClick={aiDescribe} disabled={aiBusy} className={aiBtn} data-testid="task-ai-describe"><Sparkles size={14} />AI describe</button>
              <button onClick={aiPriority} disabled={aiBusy} className={aiBtn} data-testid="task-ai-priority"><Flame size={14} />AI priority</button>
            </div>
          </div>
          <div><Overline>Priority</Overline><ChipRow options={PRIORITIES} value={priority} onChange={setPriority} testID="task-priority" /></div>
          <div>
            <Overline>Recurrence</Overline>
            <p className="mt-0.5 text-xs text-a-muted">Recurring tasks auto-allocate a new instance to the same assignees after manager approval.</p>
            <ChipRow options={RECURRENCE} value={recurrence} onChange={setRecurrence} testID="task-recurrence" />
          </div>
          <div>
            <Overline>Deadline (optional)</Overline>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-2 rounded-xl border border-a-border bg-a-surface px-3 py-2.5 text-[13px] font-bold text-a-text hover:border-a-primary/50" data-testid="task-deadline-date">
                    <CalendarDays size={16} className="text-a-primary" />{deadline ? deadline.toLocaleDateString() : "Pick a date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto border-a-border bg-a-surface p-0 text-a-text" align="start">
                  <Calendar mode="single" selected={deadline || undefined} disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                    onSelect={(d) => { if (!d) return; const n = new Date(d); if (deadline && includeTime) n.setHours(deadline.getHours(), deadline.getMinutes()); else n.setHours(includeTime ? 9 : 12, 0, 0, 0); setDeadline(n); setCalOpen(false); }} />
                </PopoverContent>
              </Popover>
              {deadline && includeTime ? (
                <label className="flex items-center gap-2 rounded-xl border border-a-border bg-a-surface px-3 py-2 text-[13px] font-bold text-a-text">
                  <Clock size={16} className="text-a-primary" />
                  <input type="time" value={timeValue} data-testid="task-deadline-time" className="bg-transparent outline-none"
                    onChange={(e) => { const [h, m] = e.target.value.split(":").map(Number); const n = new Date(deadline); n.setHours(h || 0, m || 0, 0, 0); setDeadline(n); }} />
                </label>
              ) : null}
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              <button onClick={() => { setIncludeTime((v) => { const nv = !v; if (nv && deadline) { const n = new Date(deadline); n.setHours(9, 0, 0, 0); setDeadline(n); } return nv; }); }} className="flex items-center gap-1 text-xs text-a-muted" data-testid="task-include-time">
                {includeTime ? <CheckSquare size={16} className="text-a-primary" /> : <Square size={16} className="text-a-primary" />}Include time
              </button>
              {deadline ? <button onClick={() => { setDeadline(null); setIncludeTime(false); }} className="ml-3 text-xs font-bold text-a-danger" data-testid="task-deadline-clear">Clear</button> : null}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="mb-2 flex justify-between"><Overline>Assign to *</Overline><span className="text-xs text-a-muted">{assignees.length} selected</span></div>
        {!canAssignOthers ? <p className="mb-2 text-xs text-a-warning">Only managers can assign to other members. You can create tasks for yourself.</p> : null}
        <div className="flex items-center gap-2 rounded-xl border border-a-border bg-a-surface px-3">
          <Search size={16} className="text-a-muted" />
          <input data-testid="task-assignee-search" placeholder="Search team members, designation, role…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-11 flex-1 bg-transparent text-sm text-a-text outline-none placeholder:text-a-muted" />
          {search ? <button onClick={() => setSearch("")} className="text-a-muted"><XCircle size={16} /></button> : null}
        </div>
        {recentMembers.length > 0 && !search ? (
          <div className="mt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[1.4px] text-a-muted">Recently assigned</p>
            <div className="flex flex-wrap gap-2">
              {recentMembers.map((m) => {
                const on = assignees.includes(m.user_id);
                return (
                  <button key={m.user_id} onClick={() => toggleAssignee(m.user_id)} className={`flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-3 text-xs font-bold ${on ? "border-a-primary bg-a-primary text-white" : "border-a-border bg-a-surface text-a-text"}`}>
                    <Avatar src={m.avatar_base64} name={m.full_name} size={22} variant="dim" />{m.full_name.split(" ")[0]}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className="mt-3 space-y-2">
          {filtered.map((m) => {
            const on = assignees.includes(m.user_id);
            return (
              <button key={m.user_id} onClick={() => toggleAssignee(m.user_id)} data-testid={`assignee-${m.user_id}`}
                className={`flex w-full items-center gap-2.5 rounded-xl border p-2.5 text-left transition-colors ${on ? "border-a-primary bg-a-dim" : "border-a-border hover:border-a-primary/50"}`}>
                <Avatar src={m.avatar_base64} name={m.full_name} size={36} />
                <div className="min-w-0 flex-1"><p className="truncate font-bold text-a-text">{m.full_name}{user?.id === m.user_id ? " (you)" : ""}</p><p className="truncate text-xs text-a-muted">{(m.designation || "").trim() || m.role}</p></div>
                {on ? <CheckCircle2 size={22} className="text-a-primary" /> : <Circle size={22} className="text-a-muted" />}
              </button>
            );
          })}
          {filtered.length === 0 ? <p className="text-[13px] text-a-muted">No members match.</p> : null}
          {members.length <= 1 ? (
            <div className="mt-1.5">
              <p className="text-xs text-a-muted">Invite teammates to this workspace to start assigning them tasks.</p>
              <button onClick={() => navigate("/workspaces")} className="mt-1.5 flex items-center gap-1.5 text-xs font-bold text-a-primary"><Users size={14} />Manage team</button>
            </div>
          ) : null}
        </div>
      </Card>

      <Card>
        <Overline className="mb-2">Checklist</Overline>
        {items.map((it, i) => (
          <div key={`${it}-${i}`} className="flex items-center gap-2.5 py-1.5">
            <Circle size={18} className="text-a-muted" /><p className="flex-1 text-a-text">{it}</p>
            <button onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="text-a-danger"><X size={18} /></button>
          </div>
        ))}
        <div className="mt-1.5 flex gap-2">
          <Input testID="task-checklist-input" placeholder="Add checklist item" value={nextItem} onChangeText={setNextItem} onSubmitEditing={addItem} />
          <button onClick={addItem} data-testid="task-checklist-add" className="flex w-12 shrink-0 items-center justify-center rounded-xl bg-a-primary text-white hover:brightness-110"><Plus size={22} /></button>
        </div>
      </Card>

      <Button testID="task-create-submit" label="Create task" onPress={submit} loading={busy} size="lg" disabled={assignees.length === 0 || !title.trim()} />
    </StackPage>
  );
}
